<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';
import ProjectPreview from "@/component/ProjectPreview.vue";
import ProjectPreviewModel from "@/models/ProjectPreviewModel";
import PikuTradeComponent from "@/component/project/PikuTradeComponent.vue";
import CGJComponent from "@/component/project/CGJComponent.vue";
import ReferendumComponent from "@/component/project/ReferendumComponent.vue";
import SproogyComponent from "@/component/project/SproogyComponent.vue";
import PortfolioComponent from "@/component/project/PortfolioComponent.vue";
import NDIComponent from "@/component/project/NDIComponent.vue";

import { markRaw } from 'vue';
import { usePostHog } from '@/composables/usePostHog';

@Component({
  components: {ProjectPreview},
})
export default class ProjectPreviewList extends Vue {
  public list: ProjectPreviewModel[] = [
    {pathToProject: "ndi", project: markRaw(NDIComponent), icon: "mdi-timer-sand"},
    {pathToProject: "portfolio", project: markRaw(PortfolioComponent), icon: "mdi-card-account-details-outline"},
    {pathToProject: "sproogy", project: markRaw(SproogyComponent), icon: "mdi-xml"},
    {pathToProject: "pikuTrade", project: markRaw(PikuTradeComponent), icon: "mdi-chart-line"},
    {pathToProject: "cgj", project: markRaw(CGJComponent), icon: "mdi-controller"},
    {pathToProject: "referendum", project: markRaw(ReferendumComponent), icon: "mdi-vote-outline"},
  ];

  public index: number = 0;
  public direction: 'up' | 'down' = 'down';
  public isMobile: boolean = false;
  private windowWidth: number = window.innerWidth;

  private isAnimating: boolean = false;
  private lastScrollTime: number = 0;
  private animationTimeout: any = null;
  private posthog = usePostHog().posthog;

  // Touch handling
  private touchStartY: number = 0;
  private touchStartX: number = 0;

  // IntersectionObserver pour savoir si le composant est visible à l'écran
  private intersectionObserver: IntersectionObserver | null = null;
  private isIntersecting: boolean = false;

  get projectLabels(): string[] {
    return [
      this.$t('about.projects.ndi.title'),
      this.$t('about.projects.portfolio.title'),
      this.$t('about.projects.sproogy.title'),
      this.$t('about.projects.pikutrade.title'),
      this.$t('about.projects.cgj.title'),
      this.$t('about.projects.referendum.title'),
    ];
  }

  get tooltipPosition(): string {
    return this.windowWidth <= 1200 ? 'top' : 'left';
  }

  mounted() {
    this.detectDevice();
    window.addEventListener("resize", this.handleResize);
    this.setupIntersectionObserver();
    this.attachListeners();
  }

  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    this.detachListeners();
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }

  private setupIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          this.isIntersecting = entries[0]?.isIntersecting ?? false;
        },
        { threshold: 0.3 }
    );
    this.intersectionObserver.observe(this.$el as HTMLElement);
  }

  private handleResize() {
    this.detectDevice();
    this.windowWidth = window.innerWidth;
  }

  private attachListeners() {
    window.addEventListener("wheel", this.handleWheel, { passive: false });
    window.addEventListener("keydown", this.handleKeydown);

    // Touch
    const el = this.$el as HTMLElement;
    el.addEventListener("touchstart", this.handleTouchStart, { passive: true });
    el.addEventListener("touchmove", this.handleTouchMove, { passive: false });
    el.addEventListener("touchend", this.handleTouchEnd, { passive: true });
  }

  private detachListeners() {
    window.removeEventListener("wheel", this.handleWheel);
    window.removeEventListener("keydown", this.handleKeydown);
    const el = this.$el as HTMLElement;
    if (el) {
      el.removeEventListener("touchstart", this.handleTouchStart);
      el.removeEventListener("touchmove", this.handleTouchMove);
      el.removeEventListener("touchend", this.handleTouchEnd);
    }
  }

  private detectDevice() {
    this.isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  // ─── DESKTOP : molette ───────────────────────────────────────────
  private handleWheel(event: WheelEvent) {
    if (!this.isIntersecting) return;

    const delta = event.deltaY;

    // Sortie naturelle aux extrémités : on laisse passer le scroll
    if (this.index === 0 && delta < 0) return;
    if (this.index === this.list.length - 1 && delta > 0) return;

    // On est dans le carousel et pas aux extrémités → on bloque le scroll page
    if (event.cancelable) event.preventDefault();

    if (this.isAnimating) return;

    const now = Date.now();
    if (now - this.lastScrollTime < 600) return;
    if (Math.abs(delta) < 20) return;

    this.lastScrollTime = now;
    if (delta > 0) this.scrollApres();
    else this.scrollAvant();
  }

  private handleKeydown(event: KeyboardEvent) {
    if (!this.isIntersecting) return;
    if (this.isAnimating) return;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      if (this.index < this.list.length - 1) this.scrollApres();
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      if (this.index > 0) this.scrollAvant();
    }
  }

  // ─── MOBILE : touch ───────────────────────────────────────────
  private handleTouchStart(event: TouchEvent) {
    this.touchStartY = event.touches[0].clientY;
    this.touchStartX = event.touches[0].clientX;
  }

  private handleTouchMove(event: TouchEvent) {
    if (!this.isIntersecting) return;

    const touch = event.touches[0];
    const deltaY = this.touchStartY - touch.clientY;
    const deltaX = this.touchStartX - touch.clientX;

    // Ignorer si le geste est plus horizontal que vertical
    if (Math.abs(deltaX) > Math.abs(deltaY)) return;

    // Sortie naturelle aux extrémités : on laisse passer le scroll
    if (this.index === 0 && deltaY < 0) return;
    if (this.index === this.list.length - 1 && deltaY > 0) return;

    // On est dans le carousel et pas aux extrémités → on bloque le scroll page
    if (event.cancelable) event.preventDefault();
  }

  private handleTouchEnd(event: TouchEvent) {
    if (!this.isIntersecting) return;

    const deltaY = this.touchStartY - event.changedTouches[0].clientY;
    const deltaX = this.touchStartX - event.changedTouches[0].clientX;

    // Ignorer si le geste est plus horizontal que vertical
    if (Math.abs(deltaX) > Math.abs(deltaY)) return;

    // Seuil minimal pour déclencher un swipe (assez grand pour ne pas interférer avec un scroll normal)
    if (Math.abs(deltaY) < 60) return;

    const now = Date.now();
    if (now - this.lastScrollTime < 500) return;
    if (this.isAnimating) return;

    this.lastScrollTime = now;

    if (deltaY > 0 && this.index < this.list.length - 1) {
      this.scrollApres();
    } else if (deltaY < 0 && this.index > 0) {
      this.scrollAvant();
    }
  }

  // ─── Navigation ──────────────────────────────────────────────────
  public modulo(n: number, total: number): number {
    return ((n % total) + total) % total;
  }

  private scrollAvant() {
    this.direction = 'up';
    this.index = this.modulo(this.index - 1, this.list.length);
    this.startAnimation();
  }

  private scrollApres() {
    this.direction = 'down';
    this.index = this.modulo(this.index + 1, this.list.length);
    this.startAnimation();
  }

  private scrollToIndex(dotIndex: number) {
    if (dotIndex === this.index) return;
    this.posthog.capture('project_carousel_navigated', { target_project: this.list[dotIndex].pathToProject, index: dotIndex });
    this.direction = dotIndex > this.index ? 'down' : 'up';
    this.index = dotIndex;
    this.startAnimation();
  }

  private startAnimation() {
    if (this.animationTimeout) clearTimeout(this.animationTimeout);
    this.isAnimating = true;
    this.animationTimeout = setTimeout(() => {
      this.isAnimating = false;
      this.animationTimeout = null;
    }, 600);
  }
}
</script>

<template>
  <div class="list-container">
    <div class="preview-cards-wrapper">
      <div class="preview-cards">
        <transition :name="'carousel-' + direction">
          <div class="card-item" :key="list[index].pathToProject">
            <project-preview
                :model="list[index]"
                :can-redirect="true"
                class="front-card"
                :is-preview="true"
            />
          </div>
        </transition>
      </div>
    </div>

    <div class="project-navigator ln-glass-elevated" :class="{ 'navigator-hidden': isMobile && !isIntersecting }">
      <div v-for="(item, dotIndex) in list"
           :key="dotIndex"
           class="nav-item"
           :class="{ 'active': dotIndex === index }"
           @click="scrollToIndex(dotIndex)"
           v-tooltip:[tooltipPosition]="projectLabels[dotIndex]"
           role="button">

        <span class="mdi" :class="item.icon"></span>

        <div v-if="dotIndex === index" class="scroll-ring">
          <svg viewBox="0 0 36 36">
            <path class="ring-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
            <path class="ring-progress"
                  style="stroke-dasharray: 100, 100"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.list-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100vh - var(--ln-nav-offset));
  width: 100%;
  position: relative;
  /* Pas d'overflow:hidden ici — ça capturerait les événements touch et bloquerait le scroll page */
  background: radial-gradient(circle at center, rgba(44, 122, 123, 0.05) 0%, transparent 70%);
}

@media (max-width: 768px) {
  .list-container {
    height: calc(100vh - var(--ln-nav-offset));
  }
}

.preview-cards-wrapper {
  position: relative;
  width: 100%;
  height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .preview-cards-wrapper {
    height: 72vh;
    margin-bottom: 4rem;
  }
}

.preview-cards {
  position: relative;
  z-index: 10;
  width: 60%;
  max-width: 900px;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.card-item {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.card-item :deep(.project-card) {
  height: 100% !important;
  max-height: 100% !important;
}

.carousel-down-enter-active,
.carousel-up-enter-active {
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.carousel-down-leave-active,
.carousel-up-leave-active {
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  position: absolute !important;
  width: 100%;
}

.carousel-down-enter-from {
  opacity: 0;
  transform: translateY(100vh);
}
.carousel-down-leave-to {
  opacity: 0;
  transform: translateY(-100vh);
}

.carousel-up-enter-from {
  opacity: 0;
  transform: translateY(-100vh);
}
.carousel-up-leave-to {
  opacity: 0;
  transform: translateY(100vh);
}

.front-card {
  box-shadow: 0 40px 100px rgba(0,0,0,0.15);
  width: 100%;
}

.project-navigator {
  position: absolute;
  right: 3rem;
  top: 50%;
  transform: translateY(-50%);
  padding: 0.8rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  border-radius: 3rem;
  border: 1px solid var(--ln-glass-border-strong);
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
  z-index: 100;
}

.nav-item {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  position: relative;
  transition: all var(--ln-transition-normal) var(--ln-ease-bounce);
  background: var(--ln-glass-surface);
  color: var(--ln-text-muted);
  font-size: 1.2rem;
}

.nav-item:hover {
  transform: scale(1.15);
  color: var(--ln-teal-deep);
  background: var(--ln-glass-elevated);
}

.nav-item.active {
  color: var(--ln-teal-deep);
  background: var(--ln-glass-elevated);
  transform: scale(1.1);
  box-shadow: 0 0 15px rgba(44, 122, 123, 0.2);
}

@media (max-width: 1200px) {
  .project-navigator {
    right: 50% !important;
    top: auto !important;
    bottom: 2.5rem !important;
    transform: translateX(50%) !important;
    flex-direction: row !important;
    padding: 0.6rem !important;
    gap: 0.8rem !important;
  }

  .nav-item {
    width: 38px;
    height: 38px;
    font-size: 1.1rem;
  }

  .preview-cards {
    width: 85% !important;
  }
}

@media (max-width: 768px) {
  .preview-cards-wrapper { height: 72vh; margin-bottom: 4rem; }
  .preview-cards { width: 95% !important; }
  .project-navigator {
    position: fixed !important;
    left: 50% !important;
    right: auto !important;
    bottom: 0 !important;
    top: auto !important;
    transform: translateX(-50%) !important;
    flex-direction: row !important;
    gap: 0.8rem !important;
    padding: 0.6rem 1rem !important;
    padding-bottom: calc(0.6rem + env(safe-area-inset-bottom)) !important;
    border-radius: 1.5rem 1.5rem 0 0 !important;
    z-index: 200;
    /* Transition slide depuis le bas */
    transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.35s ease !important;
    opacity: 1;
  }
  .project-navigator.navigator-hidden {
    transform: translateX(-50%) translateY(110%) !important;
    opacity: 0;
    pointer-events: none;
  }
  .nav-item {
    width: 38px !important;
    height: 38px !important;
    font-size: 1.1rem !important;
  }
  .bg-hint { display: none; }
}

.scroll-ring {
  position: absolute;
  inset: -4px;
  z-index: 1;
}

.scroll-ring svg {
  width: 100%;
  height: 100%;
}

.ring-bg {
  fill: none;
  stroke: var(--ln-glass-border);
  stroke-width: 2;
}

.ring-progress {
  fill: none;
  stroke: var(--ln-teal-deep);
  stroke-width: 2.5;
  stroke-linecap: round;
  transition: stroke-dasharray 0.3s ease;
}
</style>